import { useCallback, useEffect, useState } from 'react';
import { Activity, CheckCircle2, CircleSlash, RefreshCw, XCircle, Layers } from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import { useApi } from '../contexts/ApiContext';
import type { ApiResponse } from '../types';
import PageHeader from '../components/ui/PageHeader';
import { SkeletonCard } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/Toast';

interface Service {
  service: string;
  name: string;
  purpose: string;
  status: 'ok' | 'failing' | 'unconfigured' | 'unchecked';
  detail: string | null;
  last_error: string | null;
  error_kind: 'auth' | 'unreachable' | 'quota' | 'error' | null;
  consecutive_failures: number;
  failing_since: string | null;
  last_success_at: string | null;
  last_checked_at: string;
}

interface Data {
  services: Service[];
  cove: { base_url: string | null; api_key_set: boolean };
}

const KIND_LABEL: Record<string, string> = {
  auth: 'Credentials rejected',
  unreachable: 'Unreachable',
  quota: 'Out of credits',
  error: 'Error',
};

const ago = (iso: string | null) => (iso ? formatDistanceToNowStrict(new Date(iso), { addSuffix: true }) : 'never');

/** Super-admin page: health of every external service, and Cove's connection. */
export default function IntegrationsPage() {
  const { fetchApi } = useApi();
  const toast = useToast();
  const [data, setData] = useState<Data | null>(null);
  const [busy, setBusy] = useState<'check' | 'sync' | 'save' | null>(null);
  const [url, setUrl] = useState('');
  const [key, setKey] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetchApi<ApiResponse<Data>>('/integrations');
      if (r.data) { setData(r.data); setUrl(r.data.cove.base_url ?? ''); }
    } catch (e) {
      toast.error('Could not load integrations', e instanceof Error ? e.message : undefined);
    }
  }, [fetchApi, toast]);

  useEffect(() => { load(); }, [load]);

  const checkNow = async () => {
    setBusy('check');
    try {
      const r = await fetchApi<ApiResponse<{ services: Service[] }>>('/integrations/check', { method: 'POST' });
      if (r.data) setData((d) => (d ? { ...d, services: r.data!.services } : d));
      const failing = r.data?.services.filter((s) => s.status === 'failing').length ?? 0;
      if (failing) toast.error(`${failing} service${failing === 1 ? '' : 's'} failing`, 'See the details below.');
      else toast.success('All configured services are healthy');
    } catch (e) {
      toast.error('Check failed', e instanceof Error ? e.message : undefined);
    } finally { setBusy(null); }
  };

  const saveCove = async () => {
    setBusy('save');
    try {
      const body: Record<string, string | null> = { base_url: url.trim() || null };
      if (key.trim()) body.api_key = key.trim();
      await fetchApi('/integrations/cove', { method: 'PUT', body: JSON.stringify(body) });
      setKey('');
      toast.success('Cove settings saved');
      await checkNow();
    } catch (e) {
      toast.error('Could not save Cove settings', e instanceof Error ? e.message : undefined);
    } finally { setBusy(null); }
  };

  const syncCove = async () => {
    setBusy('sync');
    try {
      const r = await fetchApi<ApiResponse<{ sites: number; matched: number; changed: number; geo_filled?: number; geo_mismatch?: number }>>('/integrations/cove/sync', { method: 'POST' });
      toast.success('Synced from Cove', `${r.data?.sites} sites · ${r.data?.matched} matched · ${r.data?.changed} tiers changed · ${r.data?.geo_filled ?? 0} geos filled · ${r.data?.geo_mismatch ?? 0} geo mismatches`);
      await load();
    } catch (e) {
      toast.error('Cove sync failed', e instanceof Error ? e.message : undefined);
      await load();
    } finally { setBusy(null); }
  };

  if (!data) return <div className="space-y-6"><PageHeader title="Integrations" /><SkeletonCard lines={6} /><SkeletonCard lines={4} /></div>;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Integrations"
        description="External services DomainHQ relies on. Checked hourly and right after each deploy; a failing service raises an urgent notification (registrar APIs are tracked per account under Registrars)."
        actions={
          <button className="btn btn-secondary" onClick={checkNow} disabled={busy !== null}>
            <RefreshCw className={`mr-2 h-4 w-4 ${busy === 'check' ? 'animate-spin' : ''}`} />Check now
          </button>
        }
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {data.services.map((s) => (
          <div key={s.service} className={`card p-5 ${s.status === 'failing' ? 'border-red-200' : ''}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-slate-900">{s.name}</h2>
                <p className="text-sm text-slate-500">{s.purpose}</p>
              </div>
              {s.status === 'ok' && <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"><CheckCircle2 className="h-3.5 w-3.5" />Healthy</span>}
              {s.status === 'failing' && <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700"><XCircle className="h-3.5 w-3.5" />{KIND_LABEL[s.error_kind ?? 'error']}</span>}
              {s.status === 'unchecked' && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500"><RefreshCw className="h-3.5 w-3.5" />Not checked yet</span>}
              {s.status === 'unconfigured' && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"><CircleSlash className="h-3.5 w-3.5" />Not configured</span>}
            </div>
            {s.status === 'failing' && s.last_error && (
              <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{s.last_error}</p>
            )}
            {s.detail && s.status !== 'failing' && <p className="mt-3 text-sm text-slate-600">{s.detail}</p>}
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500">
              <dt>Last success</dt><dd className="text-right text-slate-700">{ago(s.last_success_at)}</dd>
              <dt>Last checked</dt><dd className="text-right text-slate-700">{ago(s.last_checked_at || null)}</dd>
              {s.status === 'failing' && (<><dt>Failing since</dt><dd className="text-right text-red-700">{ago(s.failing_since)} · {s.consecutive_failures}×</dd></>)}
            </dl>
          </div>
        ))}
      </div>

      <div className="card p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><Layers className="h-4 w-4 text-slate-400" />Cove</h2>
            <p className="mt-0.5 max-w-2xl text-sm text-slate-500">
              Source of each domain's <strong>Tier</strong> (read-only in DomainHQ) and <strong>target geos</strong> (fills an empty Geo; a different one is flagged as a mismatch), pulled daily at 05:00 UTC from Cove's <code className="text-xs">/api/v1/sites</code>.
              A Cove API key sees only what its owner can see, so create it in Cove (Settings → API) as someone with access to <strong>all sites</strong>.
            </p>
          </div>
          <button className="btn btn-secondary" onClick={syncCove} disabled={busy !== null || !data.cove.base_url || !data.cove.api_key_set}>
            <Activity className={`mr-2 h-4 w-4 ${busy === 'sync' ? 'animate-pulse' : ''}`} />{busy === 'sync' ? 'Syncing…' : 'Sync from Cove now'}
          </button>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="label" htmlFor="cove-url">Cove URL</label>
            <input id="cove-url" className="input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://cove.example.com" />
          </div>
          <div>
            <label className="label" htmlFor="cove-key">{data.cove.api_key_set ? 'Replace API key' : 'API key'}</label>
            <input id="cove-key" type="password" autoComplete="off" className="input" value={key} onChange={(e) => setKey(e.target.value)}
              placeholder={data.cove.api_key_set ? '•••••••• (saved — paste a new one to replace)' : 'Cove API key'} />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button className="btn btn-primary" onClick={saveCove} disabled={busy !== null || (!key.trim() && url.trim() === (data.cove.base_url ?? ''))}>
            {busy === 'save' ? 'Saving…' : 'Save and test'}
          </button>
          <p className="text-xs text-slate-500">The key is stored encrypted and never shown again.</p>
        </div>
      </div>
    </div>
  );
}
